import React, { FunctionComponent, useEffect, useState } from "react";
import { get } from "../../api";

interface FrontEndVersionResponse {
  release: string;
  commit: string;
}

interface BeneDbInfo {
  statuses: Record<string, unknown>;
  app_db_version: number;
  db_version: number;
  db_created_date?: string;
}

interface DrugDbInfo {
  statuses: Record<string, unknown>;
  versions: Record<string, string>;
  app_db_version: number;
  db_version: number;
  db_created_dates: Record<string, string>;
  db_submission_windows: Record<string, string>;
}

interface PlanDbInfo {
  statuses: Record<string, unknown>;
  app_db_version: number;
  db_version: number;
  db_created_date: string;
}

interface ApiVersionResponse {
  commit: string;
  release: string;
  planinfo: PlanDbInfo;
  druginfo: DrugDbInfo;
  beneinfo: BeneDbInfo;
}

interface VersionInfo {
  feRelease?: string;
  feCommit?: string;
  apiRelease?: string;
  apiCommit?: string;
  planInfo?: PlanDbInfo;
  beneInfo?: BeneDbInfo;
  drugInfo?: DrugDbInfo;
}

const VersionPage: FunctionComponent = (): JSX.Element => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo>({});

  const getVersionInfo = async (): Promise<void> => {
    const feRes = await fetch(`${window.location.origin}/plan-compare/version`);

    let feCommit = "";
    let feRelease = "";

    try {
      const { release, commit }: FrontEndVersionResponse = await feRes.json();

      feCommit = commit;
      feRelease = release;
    } catch (e) {
      console.log(e);
    }

    const res = await get("/status", {});
    const apiRes: ApiVersionResponse = (res.body as unknown) as ApiVersionResponse;

    const {
      beneinfo,
      commit: apiCommit,
      druginfo,
      planinfo,
      release: apiRelease,
    } = apiRes;

    setVersionInfo({
      ...versionInfo,
      feRelease,
      feCommit,
      apiRelease,
      apiCommit,
      planInfo: planinfo,
      drugInfo: druginfo,
      beneInfo: beneinfo,
    });
  };

  useEffect(() => {
    getVersionInfo();

    const footer = document.querySelector(".m-c-footer");

    if (footer) {
      footer.classList.add("ds-u-display--none");
    }

    return (): void => {
      if (footer) {
        footer.classList.remove("ds-u-display--none");
      }
    };
  }, []);

  return (
    <div className="ds-u-margin-x--5">
      <h2 className="ds-u-margin-bottom--0">Frontend</h2>
      <p className="ds-u-margin-top--0">
        Release: {versionInfo.feRelease}
        <br />
        Commit: {versionInfo.feCommit}
      </p>
      <h2 className="ds-u-margin-bottom--0">API</h2>
      <p className="ds-u-margin-top--0">
        Release: {versionInfo.apiRelease}
        <br />
        Commit: {versionInfo.apiCommit}
      </p>
      <h2 className="ds-u-margin-bottom--0">Databases</h2>
      <table className="ds-c-table ds-l-col--5">
        <thead>
          <tr>
            <th>Name</th>
            <th>Version</th>
            <th>Created Date</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>druginfo</td>
            <td>
              <ul className="ds-c-list ds-c-list--bare">
                {Object.keys(versionInfo.drugInfo?.versions || {}).map(year => {
                  const submissionWindow = `(submission window: ${versionInfo.drugInfo?.db_submission_windows[year]})`;
                  return (
                    <li key={year}>
                      {year}: {versionInfo.drugInfo?.versions[year]}{" "}
                      {submissionWindow}
                    </li>
                  );
                })}
              </ul>
            </td>
            <td>
              <ul className="ds-c-list ds-c-list--bare">
                {Object.keys(versionInfo.drugInfo?.db_created_dates || {}).map(
                  year => {
                    return (
                      <li key={year}>
                        {year}: {versionInfo.drugInfo?.db_created_dates[year]}
                      </li>
                    );
                  }
                )}
              </ul>
            </td>
          </tr>
          <tr>
            <td>beneinfo</td>
            <td>{versionInfo.beneInfo?.db_version}</td>
            <td>{versionInfo.beneInfo?.db_created_date || ""}</td>
          </tr>
          <tr>
            <td>planinfo</td>
            <td>{versionInfo.planInfo?.db_version}</td>
            <td>{versionInfo.planInfo?.db_created_date}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default VersionPage;
